import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../react-core/Html/CheckboxEditor";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { UnavailabilityReason } from "../../../../../../Wrap/Constants/Enums/Domain/UnavailabilityReason";
import { FlexiConversionUnavailabilityReason } from "../../../../../../Wrap/Constants/Enums/Drawdown/FlexiConversionUnavailabilityReason";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { CappedPensionArrangementsFlexiConversionAvailability } from "../../../../../../Wrap/Models/Domains/Drawdown/CappedPensionArrangementsFlexiConversionAvailability";
import { AccountConversion } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/AccountConversion";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../Shared/Table/Table";
import { unavailabilityReasonsMap } from "../../ConvertCappedToFlexiWizard/Resource/Content";

interface IAccountsConversionTableProps extends IProcessComponentProps {
    isReviewStep: boolean;
}

export type AvailableArrangement = {
    arrangementDetails: CappedPensionArrangementsFlexiConversionAvailability;
    isSelected: boolean;
};
interface IAccountsConversionTableState {}

export class AccountsConversionTable extends ProcessComponent<
    IAccountsConversionTableProps,
    IAccountsConversionTableState
> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    private getAccountsAvailableForConversion(): AvailableArrangement[] {
        const { data } = this.props;
        let arrangementsToConvert: number[] = data.instruction?.CappedPensionArrangementIds;
        let accountsAvailable: AvailableArrangement[] = data.arrangementDetails
            ?.filter((arrangement) => arrangement.IsConvertibleToFlexi == true)
            .map(
                (arrangement): AvailableArrangement => {
                    return {
                        arrangementDetails: arrangement,
                        isSelected:
                            arrangementsToConvert !== undefined &&
                            arrangementsToConvert?.indexOf(arrangement.PensionArrangementId) !== -1,
                    };
                }
            );
        return accountsAvailable;
    }

    private getAccountsNotAvailableForConversion(): CappedPensionArrangementsFlexiConversionAvailability[] {
        const { data } = this.props;
        let accountsNotAvailable = data.arrangementDetails?.filter(
            (arrangement) => arrangement.IsConvertibleToFlexi == false
        );
        return accountsNotAvailable;
    }

    private renderNotAvailableAccounts() {
        let accountsNotAvailableForConversion = this.getAccountsNotAvailableForConversion();
        if (accountsNotAvailableForConversion == undefined || accountsNotAvailableForConversion.length == 0) {
            return <Alert alertType={AlertType.Warning} message={"All arrangements are available for conversion."} />;
        }
        const col = [
            {
                title: "Arrangement ID",
                key: "PensionArrangementId",
                render: (data: string) => {
                    return data;
                },
            },
            {
                title: "Post-retirement arrangement value",
                key: "EstimatedPostRetirementValue",
                render: (data: Money) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data.Amount), CurrencyCode.GBP);
                },
            },
            {
                title: "Unavailability reasons",
                key: "UnavailabilityReasons",
                render: (data: Array<FlexiConversionUnavailabilityReason>) => {
                    let reasons = "";
                    for (let i = 0; i < data.length; i++) {
                        if (i == 0) {
                            reasons = unavailabilityReasonsMap[data[i]];
                        } else {
                            reasons = reasons + ", " + unavailabilityReasonsMap[data[i]];
                        }
                    }
                    return reasons;
                },
            },
        ];
        return <Table col={col} data={accountsNotAvailableForConversion} />;
    }

    private renderAvailableAccounts() {
        let accountsAvailableForConversion = this.getAccountsAvailableForConversion();

        const { isReviewStep } = this.props;
        if (accountsAvailableForConversion == undefined || accountsAvailableForConversion.length == 0) {
            return <Alert alertType={AlertType.Warning} message={"No arrangements are available for conversion"} />;
        }
        const col = [
            {
                title: "Arrangement ID",
                key: "arrangementDetails.PensionArrangementId",
                render: (data: string) => {
                    return data;
                },
            },
            {
                title: "Post-retirement arrangement value",
                key: "arrangementDetails.EstimatedPostRetirementValue",
                render: (data: Money) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data.Amount), CurrencyCode.GBP);
                },
            },
            !isReviewStep
                ? {
                      title: "GAD maximum",
                      key: "arrangementDetails.GadMaximum",
                      render: (data: Money) => {
                          return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data.Amount), CurrencyCode.GBP);
                      },
                  }
                : null,
            !isReviewStep
                ? {
                      title: "Pre-A-day portion",
                      key: "arrangementDetails.ADayValuePercentage",
                      render: (data: number) => {
                          return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data), CurrencyCode.GBP);
                      },
                  }
                : null,
            {
                title: "Inherited?",
                key: "arrangementDetails.IsInherited",
                render: (data: boolean) => {
                    return data ? "Yes" : "No";
                },
            },
            !isReviewStep
                ? {
                      title: "Convert",
                      key: "isSelected",
                      render: (data: boolean, row, index) => {
                          return (
                              <CheckboxEditor
                                  cssWrapper="mt-20 ml-20"
                                  value={data}
                                  onChange={(v) => this.onAccountSelectionChange(v, index)}
                              />
                          );
                      },
                  }
                : null,
        ];
        if (isReviewStep) {
            accountsAvailableForConversion = accountsAvailableForConversion.filter((a) => a.isSelected == true);
        }
        return <Table col={col} data={accountsAvailableForConversion} />;
    }

    private onAccountSelectionChange(isSelected: boolean, index: number) {
        const accountsAvailableForConversion = this.getAccountsAvailableForConversion();
        accountsAvailableForConversion[index].isSelected = isSelected;
        const selectedArrangementIds: number[] = accountsAvailableForConversion
            .filter((arrangement) => arrangement.isSelected == true)
            .map((selectedArrangement) => selectedArrangement.arrangementDetails.PensionArrangementId);
        this.props.onChange(selectedArrangementIds);
    }

    public render(): JSX.Element {
        const { isReviewStep } = this.props;
        const AvailableArrangementTitle = isReviewStep
            ? "Select arrangements to convert"
            : "Arrangements available for conversion";

        return (
            <div>
                {!isReviewStep && (
                    <div className="mt-40">
                        <h2>Arrangements not available for conversion</h2>
                        {this.renderNotAvailableAccounts()}
                    </div>
                )}
                <div className="mt-40">
                    <h2>{AvailableArrangementTitle}</h2>
                    {this.renderAvailableAccounts()}
                </div>
            </div>
        );
    }
}
